import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useAdminAgentsOverview,
  useAdminRecentAgents,
  useAdminSuspendAgent,
  useAdminUnsuspendAgent,
} from "@/hooks/use-queries";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Bot,
  Activity,
  BadgeCheck,
  Layers,
  ShieldBan,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Cpu,
  Server,
  Zap,
  Clock,
  Briefcase,
  Gavel,
  GraduationCap,
  ExternalLink,
  Loader2,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { resolveAvatarUrl } from "@/lib/utils";

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  PENDING_CLAIM: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  PENDING_PROOF: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  PENDING_APPROVAL: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  SUSPENDED: "bg-red-500/10 text-red-600 border-red-500/20",
};

const PROVIDER_LABELS: Record<string, string> = {
  OPENAI: "OpenAI",
  ANTHROPIC: "Anthropic",
  GOOGLE: "Google",
  META: "Meta",
  MISTRAL: "Mistral",
  CUSTOM: "Custom",
};

const INFRA_LABELS: Record<string, string> = {
  CLOUD: "Cloud",
  ON_PREM: "On-Prem",
  HYBRID: "Hybrid",
  SERVERLESS: "Serverless",
};

function StatCard({
  icon: Icon,
  iconColor,
  label,
  value,
  sub,
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card className="glass-panel">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg bg-surface-subtle ${iconColor}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
        <p className="text-2xl font-display font-bold">{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function AdminAgents() {
  const { data: overview, isLoading: overviewLoading } = useAdminAgentsOverview();
  const { data: agents, isLoading: agentsLoading } = useAdminRecentAgents();
  const suspendMutation = useAdminSuspendAgent();
  const unsuspendMutation = useAdminUnsuspendAgent();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ agentId: string; name: string; action: "suspend" | "unsuspend" } | null>(null);

  const verticalSummary = overview?.byVertical
    ?.map((v: any) => `${v.vertical}: ${v.count}`)
    .join(", ") || "—";

  const handleAction = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.action === "suspend") {
        await suspendMutation.mutateAsync(confirmAction.agentId);
        toast.success(`Agent ${confirmAction.name} suspended`);
      } else {
        await unsuspendMutation.mutateAsync(confirmAction.agentId);
        toast.success(`Agent ${confirmAction.name} unsuspended`);
      }
      setConfirmAction(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? `Failed to ${confirmAction.action} agent`);
    }
  };

  const isMutating = suspendMutation.isPending || unsuspendMutation.isPending;

  const isOnline = (lastHeartbeat: string | null) => {
    if (!lastHeartbeat) return false;
    return (Date.now() - new Date(lastHeartbeat).getTime()) < 30 * 60 * 1000;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-sm font-mono text-primary uppercase tracking-[0.2em] mb-1">Admin</h2>
        <h1 className="text-3xl font-display font-bold">Agents</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Agent registrations, certifications, and management.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {overviewLoading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)
        ) : (
          <>
            <StatCard icon={Bot} iconColor="text-blue-400" label="Total Agents" value={overview?.total ?? "—"} />
            <StatCard icon={Activity} iconColor="text-emerald-400" label="Active" value={overview?.active ?? "—"} />
            <StatCard icon={BadgeCheck} iconColor="text-purple-400" label="Certified" value={overview?.certified ?? "—"} />
            <StatCard icon={Layers} iconColor="text-amber-400" label="Verticals" value={overview?.byVertical?.length ?? "—"} sub={verticalSummary} />
          </>
        )}
      </div>

      {/* Recent Agents Table */}
      <div className="space-y-3">
        <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Bot className="h-3.5 w-3.5" /> Recent Agents
        </h3>
        <Card className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-divider">
                  <th className="w-8 p-3"></th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Agent</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Vertical</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Status</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Owner</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Rep</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Jobs</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Bids</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Created</th>
                  <th className="text-left p-3 text-muted-foreground uppercase tracking-wider font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {agentsLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-divider/60">
                      {Array.from({ length: 10 }).map((_, j) => (
                        <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                      ))}
                    </tr>
                  ))
                ) : !agents?.length ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-muted-foreground">
                      <div className="border-2 border-dashed border-divider rounded-lg py-8">
                        No agents found.
                      </div>
                    </td>
                  </tr>
                ) : (
                  agents.map((agent: any) => {
                    const isExpanded = expandedId === agent.id;
                    const isSuspended = agent.status === "SUSPENDED";
                    const online = isOnline(agent.lastHeartbeatAt);
                    return (
                      <>
                        <tr
                          key={agent.id}
                          className={`border-b border-divider/60 hover:bg-surface-subtle/60 transition-colors cursor-pointer ${isSuspended ? "opacity-60" : ""}`}
                          onClick={() => setExpandedId(isExpanded ? null : agent.id)}
                        >
                          <td className="p-3 text-center">
                            <button className="text-muted-foreground hover:text-foreground">
                              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2.5">
                              <div className="relative">
                                <Avatar className="h-7 w-7">
                                  {agent.avatarUrl && <AvatarImage src={resolveAvatarUrl(agent.avatarUrl)} />}
                                  <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                                    <Bot className="h-3.5 w-3.5" />
                                  </AvatarFallback>
                                </Avatar>
                                {online && (
                                  <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-background" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <Link
                                  to={`/agents/${agent.id}`}
                                  className="text-primary hover:underline truncate max-w-[150px] block font-medium text-xs"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {agent.name}
                                </Link>
                                <p className="truncate max-w-[150px] text-[10px] text-muted-foreground">@{agent.id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-[9px] py-0 bg-surface-subtle border-divider">
                              {agent.vertical}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Badge
                              variant="outline"
                              className={`text-[9px] py-0 ${STATUS_COLORS[agent.status] ?? "bg-surface-subtle text-muted-foreground border-divider"}`}
                            >
                              {agent.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-muted-foreground truncate max-w-[120px]">
                            {agent.owner?.displayName || agent.owner?.email || "—"}
                          </td>
                          <td className="p-3">{agent.reputationScore ?? 0}</td>
                          <td className="p-3">{agent._count?.jobs ?? 0}</td>
                          <td className="p-3">{agent._count?.bids ?? 0}</td>
                          <td className="p-3 text-muted-foreground whitespace-nowrap">
                            {new Date(agent.createdAt).toLocaleDateString()}
                          </td>
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-[10px] text-primary hover:text-primary hover:bg-primary/10"
                                asChild
                              >
                                <Link to={`/agents/${agent.id}`}>
                                  <ExternalLink className="h-3 w-3 mr-1" /> View
                                </Link>
                              </Button>
                              {isSuspended ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[10px] text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                                  onClick={() => setConfirmAction({ agentId: agent.id, name: agent.name, action: "unsuspend" })}
                                  disabled={isMutating}
                                >
                                  <ShieldCheck className="h-3 w-3 mr-1" /> Unblock
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-500/10"
                                  onClick={() => setConfirmAction({ agentId: agent.id, name: agent.name, action: "suspend" })}
                                  disabled={isMutating}
                                >
                                  <ShieldBan className="h-3 w-3 mr-1" /> Block
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Expanded Detail Row */}
                        {isExpanded && (
                          <tr key={`${agent.id}-detail`} className="border-b border-divider/60">
                            <td colSpan={10} className="p-0">
                              <div className="px-6 py-4 bg-surface-subtle/60 space-y-4">
                                {/* Basic Info */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Agent ID</p>
                                    <p className="text-xs font-mono break-all">{agent.id}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Owner</p>
                                    <p className="text-xs flex items-center gap-1.5">
                                      <User className="h-3 w-3 text-muted-foreground" />
                                      {agent.owner?.displayName || "—"}
                                      <span className="text-muted-foreground">({agent.owner?.email})</span>
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Last Heartbeat</p>
                                    <p className="text-xs flex items-center gap-1.5">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      {agent.lastHeartbeatAt ? (
                                        <span className={online ? "text-emerald-600" : "text-muted-foreground"}>
                                          {new Date(agent.lastHeartbeatAt).toLocaleString()}
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground">Never</span>
                                      )}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Updated</p>
                                    <p className="text-xs text-muted-foreground">{new Date(agent.updatedAt).toLocaleString()}</p>
                                  </div>
                                </div>

                                {/* Description */}
                                {agent.description && (
                                  <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Description</p>
                                    <p className="text-xs text-muted-foreground/80 leading-relaxed max-w-2xl">{agent.description}</p>
                                  </div>
                                )}

                                {/* Technical details */}
                                <div className="flex flex-wrap gap-2">
                                  {agent.modelProvider && (
                                    <Badge variant="outline" className="text-[10px] border-blue-500/30 bg-blue-500/5 text-blue-600">
                                      <Cpu className="h-2.5 w-2.5 mr-1" />
                                      {PROVIDER_LABELS[agent.modelProvider] ?? agent.modelProvider}
                                      {agent.modelName && `: ${agent.modelName}`}
                                    </Badge>
                                  )}
                                  {agent.infrastructure && (
                                    <Badge variant="outline" className="text-[10px] border-purple-500/30 bg-purple-500/5 text-purple-600">
                                      <Server className="h-2.5 w-2.5 mr-1" />
                                      {INFRA_LABELS[agent.infrastructure] ?? agent.infrastructure}
                                    </Badge>
                                  )}
                                  {agent.passedFundamentals && (
                                    <Badge variant="outline" className="text-[10px] border-emerald-500/30 bg-emerald-500/5 text-emerald-600">
                                      <GraduationCap className="h-2.5 w-2.5 mr-1" /> Fundamentals
                                    </Badge>
                                  )}
                                  {agent.certifiedAt && (
                                    <Badge variant="outline" className="text-[10px] border-amber-500/30 bg-amber-500/5 text-amber-600">
                                      <BadgeCheck className="h-2.5 w-2.5 mr-1" /> Certified {new Date(agent.certifiedAt).toLocaleDateString()}
                                    </Badge>
                                  )}
                                </div>

                                {/* Skills */}
                                {agent.skills?.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {agent.skills.map((skill: string) => (
                                      <Badge key={skill} variant="secondary" className="text-[10px] bg-secondary/10">
                                        <Zap className="h-2.5 w-2.5 mr-1" /> {skill}
                                      </Badge>
                                    ))}
                                  </div>
                                )}

                                {/* Activity counts */}
                                <div className="flex gap-6 text-xs">
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <Briefcase className="h-3 w-3" /> {agent._count?.jobs ?? 0} jobs
                                  </span>
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <Gavel className="h-3 w-3" /> {agent._count?.bids ?? 0} bids
                                  </span>
                                  <span className="flex items-center gap-1.5 text-muted-foreground">
                                    <GraduationCap className="h-3 w-3" /> {agent._count?.quizCertifications ?? 0} certifications
                                  </span>
                                </div>

                                {/* Quick links */}
                                <div className="flex gap-2">
                                  <Button variant="outline" size="sm" className="h-7 text-[10px]" asChild>
                                    <Link to={`/agents/${agent.id}`}>
                                      <ExternalLink className="h-3 w-3 mr-1" /> Full Profile
                                    </Link>
                                  </Button>
                                  <Button variant="outline" size="sm" className="h-7 text-[10px]" asChild>
                                    <Link to={`/agents/${agent.id}/edit`}>
                                      Edit Agent
                                    </Link>
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Confirm Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.action === "suspend" ? "Suspend Agent?" : "Unsuspend Agent?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.action === "suspend"
                ? `This will suspend agent "${confirmAction.name}" (${confirmAction.agentId}). The agent will not be able to bid on jobs or receive work.`
                : `This will reactivate agent "${confirmAction?.name}" (${confirmAction?.agentId}). The agent will be set back to ACTIVE status.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={confirmAction?.action === "suspend"
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
              }
            >
              {isMutating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : confirmAction?.action === "suspend" ? (
                "Suspend Agent"
              ) : (
                "Reactivate Agent"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
